import { detectInputType } from './input-detection';

/**
 * Result of {@link detectCchsid}. `value` is the trimmed input, never case-changed
 * (a cchsid is sent verbatim — trim only).
 */
export type CchsidMatch = {
  kind: 'unambiguous' | 'candidate' | 'none';
  value: string;
};

/**
 * Formats that unambiguously identify a cchsid — a hit here skips the normal
 * search entirely and goes straight to the single-call lookup.
 *
 * | Format | Pattern |
 * |--------|---------|
 * | 32 hex chars (case-insensitive) | `^[0-9a-f]{32}$` |
 * | `webrtc-` + 12 hex | `^webrtc-[0-9a-f]{12}$` |
 * | `webrtc-playground-` + 12 hex | `^webrtc-playground-[0-9a-f]{12}$` |
 * | `websocket-` + 12 hex | `^websocket-[0-9a-f]{12}$` |
 * | `preview-` + 12 hex | `^preview-[0-9a-f]{12}$` |
 *
 * @ticket EB-4153
 */
export const UNAMBIGUOUS_CCHSID_PATTERNS: RegExp[] = [
  /^[0-9a-f]{32}$/i,
  /^webrtc-[0-9a-f]{12}$/i,
  /^webrtc-playground-[0-9a-f]{12}$/i,
  /^websocket-[0-9a-f]{12}$/i,
  /^preview-[0-9a-f]{12}$/i,
];

/**
 * Charset + length bounds for a "might be a cchsid" candidate (A4). The charset
 * excludes whitespace, so this also enforces "single token".
 */
const CANDIDATE_PATTERN = /^[A-Za-z0-9._-]{8,50}$/;
const HAS_DIGIT = /\d/;

/**
 * Classify a raw search query as a cchsid match. `unambiguous` skips the normal
 * search and jumps straight to the single-call lookup; `candidate` runs both the
 * normal search and the lookup, rendering a call entry above the results.
 *
 * A 32-hex-digit string is checked first, so it is always `unambiguous`, never
 * mistaken for the (also all-digit) candidate/phone-number cases.
 *
 * @ticket EB-4153
 */
export function detectCchsid(raw: string): CchsidMatch {
  const value = raw.trim();
  if (!value) return { kind: 'none', value };

  if (UNAMBIGUOUS_CCHSID_PATTERNS.some((pattern) => pattern.test(value))) {
    return { kind: 'unambiguous', value };
  }

  if (CANDIDATE_PATTERN.test(value) && HAS_DIGIT.test(value) && detectInputType(value) !== 'phone') {
    return { kind: 'candidate', value };
  }

  return { kind: 'none', value };
}
